import { Box, Button, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import { useFetch } from '../../../hooks/useFecth';
import MetricCard from '../components/MetricCard';
import PeriodButtonGroup from '../components/PeriodButtonGroup';

const chartTooltipSx = {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
};

const paperSx = {
    p: 3,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 'none',
};

export default function ClubsOverview() {
    const [period, setPeriod] = useState('trimestral');
    const { data: stats } = useFetch('clubs/overview', { params: { period } });

    const clubes = stats?.clubes || {};
    const evolution = stats?.evolution || {};

    const chartData = useMemo(() => {
        const created = evolution.clubes || [];
        const canceled = evolution.cancelamentos || [];
        const byMonth = new Map();

        for (const row of created) {
            byMonth.set(row.mes, { mes: row.mes, clubes: row.quantidade || 0, cancelamentos: 0 });
        }
        for (const row of canceled) {
            const current = byMonth.get(row.mes) || { mes: row.mes, clubes: 0, cancelamentos: 0 };
            current.cancelamentos = row.quantidade || 0;
            byMonth.set(row.mes, current);
        }

        return [...byMonth.values()];
    }, [evolution]);

    return (
        <Box>
            <Container maxWidth="xl">
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    gap={2}
                    pb={3}
                >
                    <Box>
                        <Typography variant="h2" gutterBottom>
                            Visão geral
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Evolução de clubes gerados e cancelados no período.
                        </Typography>
                    </Box>
                    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                        <Button size="small" variant="outlined" component={Link} to="/dashboard/clubes" startIcon={<GroupsOutlined />}>
                            Clubes
                        </Button>
                        <PeriodButtonGroup value={period} onChange={setPeriod} />
                    </Stack>
                </Stack>

                <Grid container spacing={3} mb={4}>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title="Total de clubes"
                            value={clubes.total || 0}
                            icon={<BusinessIcon />}
                            subtitle={`${clubes.ativos || 0} ativos`}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard title="Clubes ativos" value={clubes.ativos || 0} icon={<BusinessIcon />} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard title="Clubes inativos" value={clubes.inativos || 0} icon={<BusinessIcon />} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard title="Clubes cancelados" value={clubes.cancelados || 0} icon={<BusinessIcon />} />
                    </Grid>
                </Grid>

                <Paper sx={paperSx}>
                    <Typography variant="h6" mb={3} fontWeight={600}>
                        Evolução versus cancelamento
                    </Typography>
                    <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                            <Tooltip contentStyle={chartTooltipSx} />
                            <Legend />
                            <Line type="monotone" dataKey="clubes" stroke="#00D4AA" strokeWidth={2} name="Novos clubes" />
                            <Line type="monotone" dataKey="cancelamentos" stroke="#FF4757" strokeWidth={2} name="Cancelamentos" />
                        </LineChart>
                    </ResponsiveContainer>
                </Paper>
            </Container>
        </Box>
    );
}
